import { createClerkSupabaseClient } from '@/lib/supabase'
import type { ServiceAuthProps } from '@/interfaces/serviceAuth'
import pRetry from 'p-retry'

export interface UploadResult {
  path: string
  fullPath: string
  id?: string
}

export interface SignedUploadUrlResult {
  signedUrl: string
  path: string
  token: string
}

export interface DownloadUrlResult {
  signedUrl: string
}

export interface RetryOptions {
  maxRetries?: number
  baseDelay?: number
}

export interface CacheOptions {
  enabled?: boolean
  cacheName?: string
  maxAge?: number // TTL in milliseconds
}

/**
 * Pure Supabase Storage operations
 * Handles file upload, download, and deletion from Supabase Storage
 */
export class StorageService {
  private bucketName: string
  private sessionProps: ServiceAuthProps
  private retryOptions: Required<RetryOptions>
  private cacheOptions: Required<CacheOptions>

  constructor(sessionProps: ServiceAuthProps, retryOptions?: RetryOptions, cacheOptions?: CacheOptions) {
    console.log('🗄️ StorageService: Creating new instance', { userId: sessionProps.userId, orgId: sessionProps.orgId });
    this.sessionProps = sessionProps
    this.bucketName = 'project-files'
    
    // Simple retry defaults
    this.retryOptions = {
      maxRetries: retryOptions?.maxRetries ?? 3,
      baseDelay: retryOptions?.baseDelay ?? 1000
    }
    
    // Cache defaults - enabled by default
    this.cacheOptions = {
      enabled: cacheOptions?.enabled ?? true,
      cacheName: cacheOptions?.cacheName ?? 'wave-files-v1',
      maxAge: cacheOptions?.maxAge ?? 24 * 60 * 60 * 1000 // 24 hours
    }
    
    if (this.cacheOptions.enabled) {
      console.log('🗄️ StorageService Cache: Caching enabled', { cacheName: this.cacheOptions.cacheName });
    }
  }

  /**
   * Retry wrapper for fetch requests using p-retry
   */
  private async fetchWithRetry(url: string, storagePath: string): Promise<Response> {
    return pRetry(
      async () => {
        const response = await fetch(url)
        
        // Don't retry 4xx client errors (auth, not found, etc.)
        if (response.status >= 400 && response.status < 500) {
          return response
        }
        
        // Retry 5xx server errors and network issues
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        return response
      },
      {
        retries: this.retryOptions.maxRetries,
        minTimeout: this.retryOptions.baseDelay,
        onFailedAttempt: (error) => {
          const attempt = error.attemptNumber
          const total = error.retriesLeft + error.attemptNumber
          const nextDelay = this.retryOptions.baseDelay * Math.pow(2, attempt - 1)
          
          console.log(`🗄️ StorageService: File fetch failed - ${storagePath}`)
          console.log(`🗄️ StorageService: Attempt ${attempt}/${total} failed - ${error.message}`)
          
          if (error.retriesLeft > 0) {
            console.log(`🗄️ StorageService: Retrying in ${nextDelay}ms...`)
          } else {
            console.log(`🗄️ StorageService: All retry attempts exhausted`)
          }
        }
      }
    )
  }

  /**
   * Get cached file content if available and not expired
   */
  private async getCachedContent(storagePath: string): Promise<string | null> {
    if (!this.cacheOptions.enabled) {
      return null
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName)
      const cachedResponse = await cache.match(storagePath)
      
      if (!cachedResponse) {
        console.log(`🗄️ StorageService Cache: Cache miss - ${storagePath}`)
        return null
      }
      
      // Check if cache entry is expired
      const cacheDate = cachedResponse.headers.get('x-cache-date')
      if (cacheDate) {
        const age = Date.now() - parseInt(cacheDate)
        if (age > this.cacheOptions.maxAge) {
          console.log(`🗄️ StorageService Cache: Cache expired - ${storagePath}`)
          await cache.delete(storagePath)
          return null
        }
      }
      
      console.log(`🗄️ StorageService Cache: Cache hit - ${storagePath}`)
      return await cachedResponse.text()
      
    } catch (error) {
      console.warn(`🗄️ StorageService Cache: Cache read failed - ${storagePath}`, error)
      return null // Fallback to network on cache errors
    }
  }

  /**
   * Store file content in cache with timestamp
   */
  private async setCachedContent(storagePath: string, content: string): Promise<void> {
    if (!this.cacheOptions.enabled) {
      return
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName)
      
      // Create response with cache timestamp
      const response = new Response(content, {
        headers: {
          'Content-Type': 'text/plain',
          'x-cache-date': Date.now().toString()
        }
      })
      
      await cache.put(storagePath, response)
      console.log(`🗄️ StorageService Cache: Stored in cache - ${storagePath}`)
      
    } catch (error) {
      console.warn(`🗄️ StorageService Cache: Cache write failed - ${storagePath}`, error)
      // Don't throw - caching failure shouldn't break file operations
    }
  }

  /**
   * Get cached binary content (ArrayBuffer) if available and not expired
   */
  private async getCachedContentBinary(storagePath: string): Promise<ArrayBuffer | null> {
    if (!this.cacheOptions.enabled) {
      return null
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName + '-binary')
      const cachedResponse = await cache.match(storagePath)
      
      if (!cachedResponse) {
        console.log(`🗄️ StorageService Cache: Binary cache miss - ${storagePath}`)
        return null
      }
      
      // Check if cache entry is expired
      const cacheDate = cachedResponse.headers.get('x-cache-date')
      if (cacheDate) {
        const age = Date.now() - parseInt(cacheDate)
        if (age > this.cacheOptions.maxAge) {
          console.log(`🗄️ StorageService Cache: Binary cache expired - ${storagePath}`)
          await cache.delete(storagePath)
          return null
        }
      }
      
      console.log(`🗄️ StorageService Cache: Binary cache hit - ${storagePath}`)
      return await cachedResponse.arrayBuffer()
      
    } catch (error) {
      console.warn(`🗄️ StorageService Cache: Binary cache read failed - ${storagePath}`, error)
      return null // Fallback to network on cache errors
    }
  }

  /**
   * Store binary file content in cache with timestamp
   */
  private async setCachedContentBinary(storagePath: string, content: ArrayBuffer): Promise<void> {
    if (!this.cacheOptions.enabled) {
      return
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName + '-binary')
      
      // Create response with cache timestamp
      const response = new Response(content, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-cache-date': Date.now().toString()
        }
      })
      
      await cache.put(storagePath, response)
      console.log(`🗄️ StorageService Cache: Binary content cached - ${storagePath}`)
      
    } catch (error) {
      console.warn(`🗄️ StorageService Cache: Binary cache write failed - ${storagePath}`, error)
      // Non-fatal - continue execution even if caching fails
    }
  }

  /**
   * Upload file directly to Supabase Storage
   */
  async uploadFile(file: File, storagePath: string): Promise<UploadResult> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { data, error } = await supabase.storage
      .from(this.bucketName)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false, // Don't overwrite existing files
      })

    if (error) {
      console.error('🗄️ StorageService: Upload failed', error)
      throw new Error(`Failed to upload file: ${error.message}`)
    }

    return {
      path: data.path,
      fullPath: data.fullPath,
      id: data.id,
    }
  }

  /**
   * Generate signed URL for file download
   */
  async getSignedDownloadUrl(storagePath: string, expiresIn = 3600): Promise<DownloadUrlResult> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { data, error } = await supabase.storage
      .from(this.bucketName)
      .createSignedUrl(storagePath, expiresIn)

    if (error) {
      console.error('🗄️ StorageService: Failed to create signed download URL', error)
      throw new Error(`Failed to create download URL: ${error.message}`)
    }

    return {
      signedUrl: data.signedUrl,
    }
  }

  /**
   * Download file content as text for parsing with automatic retry logic and caching
   */
  async getFileContent(storagePath: string): Promise<string> {
    try {
      console.log(`🗄️ StorageService: Starting file download - ${storagePath}`)
      
      // Check cache first
      const cachedContent = await this.getCachedContent(storagePath)
      if (cachedContent) {
        return cachedContent
      }
      
      // Cache miss - proceed with network fetch
      console.log(`🗄️ StorageService Cache: Cache miss, downloading - ${storagePath}`)
      
      // Get signed URL first
      const { signedUrl } = await this.getSignedDownloadUrl(storagePath, 300) // 5 min expiry
      
      // Fetch file content with retry logic
      const response = await this.fetchWithRetry(signedUrl, storagePath)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      const content = await response.text()
      console.log(`🗄️ StorageService: File download completed - ${storagePath} (${content.length} chars)`)
      
      // Store in cache for next time
      await this.setCachedContent(storagePath, content)
      
      return content
      
    } catch (error) {
      console.error(`🗄️ StorageService: Failed to get file content - ${storagePath}`, error)
      throw new Error(`Failed to download file content: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Download file content as binary (ArrayBuffer) for XLSX files with automatic retry logic and caching
   */
  async getFileContentBinary(storagePath: string): Promise<ArrayBuffer> {
    try {
      console.log(`🗄️ StorageService: Starting binary file download - ${storagePath}`)
      
      // Check binary cache first
      const cachedContent = await this.getCachedContentBinary(storagePath)
      if (cachedContent) {
        return cachedContent
      }
      
      // Cache miss - proceed with network fetch
      console.log(`🗄️ StorageService Cache: Binary cache miss, downloading - ${storagePath}`)
      
      // Get signed URL first
      const { signedUrl } = await this.getSignedDownloadUrl(storagePath, 300) // 5 min expiry
      
      // Fetch file content with retry logic
      const response = await this.fetchWithRetry(signedUrl, storagePath)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      const content = await response.arrayBuffer()
      console.log(`🗄️ StorageService: Binary file download completed - ${storagePath} (${content.byteLength} bytes)`)
      
      // Store in binary cache for next time
      await this.setCachedContentBinary(storagePath, content)
      
      return content
      
    } catch (error) {
      console.error(`🗄️ StorageService: Failed to get binary file content - ${storagePath}`, error)
      throw new Error(`Failed to download binary file content: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Create signed upload URL for direct client uploads
   */
  async createSignedUploadUrl(storagePath: string): Promise<SignedUploadUrlResult> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    // Create signed upload URL (expires in 1 hour by default)
    const { data, error } = await supabase.storage
      .from(this.bucketName)
      .createSignedUploadUrl(storagePath)

    if (error) {
      console.error('🗄️ StorageService: Failed to create signed upload URL', error)
      throw new Error(`Upload URL creation failed: ${error.message}`)
    }

    return {
      signedUrl: data.signedUrl,
      path: storagePath,
      token: data.token,
    }
  }

  /**
   * Delete file from Supabase Storage
   */
  async deleteFile(storagePath: string): Promise<void> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { error } = await supabase.storage
      .from(this.bucketName)
      .remove([storagePath])

    if (error) {
      console.error('🗄️ StorageService: Failed to delete file', error)
      throw new Error(`Failed to delete file: ${error.message}`)
    }
  }

  /**
   * Check if file exists in storage
   */
  async fileExists(storagePath: string): Promise<boolean> {
    try {
      const supabase = createClerkSupabaseClient(this.sessionProps)
      
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .list(storagePath.substring(0, storagePath.lastIndexOf('/')), {
          search: storagePath.substring(storagePath.lastIndexOf('/') + 1)
        })

      if (error) return false
      return data && data.length > 0
    } catch {
      return false
    }
  }

  /**
   * Get file metadata without downloading
   */
  async getFileInfo(storagePath: string) {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { data, error } = await supabase.storage
      .from(this.bucketName)
      .list(storagePath.substring(0, storagePath.lastIndexOf('/')), {
        search: storagePath.substring(storagePath.lastIndexOf('/') + 1)
      })

    if (error || !data || data.length === 0) {
      throw new Error('File not found')
    }

    return data[0]
  }

  /**
   * Generate storage path for a file
   * Format: org-{organizationId}/{projectId}/{timestamp}_{randomSuffix}_{fileName}
   */
  static generateStoragePath(projectId: string, fileName: string, organizationId: string): string {
    const timestamp = Date.now()
    const randomSuffix = Math.random().toString(36).substring(2, 8)
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
    
    return `org-${organizationId}/${projectId}/${timestamp}_${randomSuffix}_${sanitizedFileName}`
  }

  /**
   * Clear all cached files
   */
  async clearCache(): Promise<void> {
    if (!this.cacheOptions.enabled) {
      console.log('🗄️ StorageService Cache: Cache not enabled, nothing to clear')
      return
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName)
      const keys = await cache.keys()
      
      await Promise.all(keys.map(key => cache.delete(key)))
      
      console.log(`🗄️ StorageService Cache: Cache cleared - ${keys.length} entries removed`)
    } catch (error) {
      console.error('🗄️ StorageService Cache: Failed to clear cache', error)
      throw new Error(`Failed to clear cache: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get cache information and statistics
   */
  async getCacheInfo(): Promise<{ entries: number; estimatedSize: string; cacheName: string; enabled: boolean }> {
    if (!this.cacheOptions.enabled) {
      return {
        entries: 0,
        estimatedSize: '0 MB',
        cacheName: this.cacheOptions.cacheName,
        enabled: false
      }
    }

    try {
      const cache = await caches.open(this.cacheOptions.cacheName)
      const keys = await cache.keys()
      
      // Estimate total size by sampling a few entries
      let totalSize = 0
      const sampleSize = Math.min(keys.length, 5) // Sample first 5 entries
      
      for (let i = 0; i < sampleSize; i++) {
        const response = await cache.match(keys[i])
        if (response) {
          const content = await response.text()
          totalSize += content.length
        }
      }
      
      // Extrapolate total size
      const averageSize = sampleSize > 0 ? totalSize / sampleSize : 0
      const estimatedTotalSize = averageSize * keys.length
      const estimatedSizeMB = (estimatedTotalSize / (1024 * 1024)).toFixed(2)
      
      console.log(`🗄️ StorageService Cache: Cache info - ${keys.length} entries, ~${estimatedSizeMB} MB`)
      
      return {
        entries: keys.length,
        estimatedSize: `${estimatedSizeMB} MB`,
        cacheName: this.cacheOptions.cacheName,
        enabled: true
      }
      
    } catch (error) {
      console.error('🗄️ StorageService Cache: Failed to get cache info', error)
      throw new Error(`Failed to get cache info: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
} 